import React from "react";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";

import ActionMenu from "components/ActionMenu";
import { MenuItem } from "components/ActionMenu/types";
import { TableHeadCell } from "components/Table/types";

import { ReactComponent as ActionsIcon } from "assets/images/actionMenu/actions-menu-icon.svg";

import SkeletonListItems from "./SkeletonListItems";

interface ListProps<T> {
  loading?: boolean;
  skeletonRowCount?: number;
  heading?: TableHeadCell<T>[];
  footer?: TableHeadCell<T>[];
  body?: TableHeadCell<T>[];
  rows: T[];
  withDivider?: boolean;
  actions?: MenuItem<T>[];
}

const ExtendedList = <T extends { id: string }>(props: ListProps<T>) => {
  const {
    heading = [],
    body = [],
    footer = [],
    loading,
    skeletonRowCount,
    withDivider,
    rows,
    actions = [],
  } = props;

  const renderItem = (
    row: T,
    column: TableHeadCell<T>,
    index: number,
    mb?: number
  ) => {
    const { cell, transform, color, id, secure } = column;
    const value = transform ? transform(row[id], row) : row[id];

    const isLastItem = index === body.length - 1;

    if (!cell && (value === undefined || value === null || value === "")) {
      return null;
    }

    return cell ? (
      cell(value, row)
    ) : (
      <Typography
        noWrap
        color={color}
        variant="body2"
        sx={{ mb: isLastItem ? 0 : mb || 1 }}
        data-posthog-mask={secure ? "true" : undefined}
      >
        {String(value)}
      </Typography>
    );
  };

  return (
    <List sx={{ width: "100%" }}>
      {loading ? (
        <SkeletonListItems
          heading={heading}
          body={body}
          rowCount={skeletonRowCount}
          withActions={actions.length > 0}
          withDivider={withDivider}
        />
      ) : (
        rows.map((row) => (
          <ListItem key={row.id}>
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ width: "85%" }}>
                  {heading.map((item, index) => (
                    <Box key={`${row.id}-${item.id as string}`}>
                      {renderItem(row, item, index, 1)}
                    </Box>
                  ))}
                </Box>
                {actions.length > 0 && (
                  <ActionMenu
                    mainIcon={<ActionsIcon />}
                    menuId="mobile-action-menu"
                    actions={actions}
                    row={row}
                  />
                )}
              </Box>
              {withDivider && (
                <Box sx={{ border: "1px solid #E5E5E5", mt: 1.5, mb: 1.5 }} />
              )}
              <Box>
                {body.map((item, index) => (
                  <Box key={`${row.id}-${item.id as string}`}>
                    {renderItem(row, item, index)}
                  </Box>
                ))}
              </Box>
              {withDivider && footer.length > 0 && (
                <>
                  {(footer.length === 1 && row[footer[0].id]) ||
                  footer.length > 1 ? (
                    <>
                      <Box
                        sx={{ border: "1px solid #E5E5E5", mt: 1.5, mb: 1.5 }}
                      />
                      <Box>
                        {footer.map((item, index) => (
                          <Box key={`${row.id}-${item.id as string}`}>
                            {renderItem(row, item, index)}
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <></>
                  )}
                </>
              )}
            </Box>
          </ListItem>
        ))
      )}
    </List>
  );
};

export default ExtendedList;
